import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';

export default function SplashScreen() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading) return;
    const timer = setTimeout(() => {
      if (user) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/welcome', { replace: true });
      }
    }, 2500);
    return () => clearTimeout(timer);
  }, [loading, user, navigate]);

  return (
    <div
      data-testid="splash-screen"
      className="fixed inset-0 flex flex-col items-center justify-center bg-white overflow-hidden"
    >
      {/* ── Top-left organic decoration ── */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: -60,
          left: -70,
          width: 260,
          height: 280,
          background: 'linear-gradient(135deg, #a7f3d0 0%, #d1fae5 60%, #ecfdf5 100%)',
          borderRadius: '38% 62% 60% 40% / 35% 30% 70% 65%',
          opacity: 0.55,
        }}
      />
      {/* ── Top-right organic decoration ── */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: -45,
          right: -55,
          width: 190,
          height: 210,
          background: 'linear-gradient(225deg, #6ee7b7 0%, #a7f3d0 55%, #d1fae5 100%)',
          borderRadius: '60% 40% 35% 65% / 50% 60% 40% 50%',
          opacity: 0.35,
        }}
      />
      {/* ── Bottom-right mini accent ── */}
      <div
        className="absolute pointer-events-none"
        style={{
          bottom: 60,
          right: -40,
          width: 140,
          height: 140,
          background: '#d1fae5',
          borderRadius: '50%',
          opacity: 0.3,
        }}
      />

      {/* ── Center content ── */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col items-center gap-3"
      >
        {/* Logo */}
        <motion.img
          src="/logo-zahbaq.png"
          alt="ZahBaq"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="w-24 h-24 object-contain"
        />

        {/* App name + subtitle */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="flex flex-col items-center gap-1.5"
        >
          <h1 className="font-playfair font-bold text-[28px] text-emerald-950 tracking-tight">
            ZahBaq
          </h1>
          <div className="flex items-center gap-3">
            <div className="h-px w-8 bg-emerald-500 rounded-full" />
            <span className="text-[11px] text-emerald-600 font-medium tracking-wider lowercase">
              sistem manajemen tahfizh
            </span>
            <div className="h-px w-8 bg-emerald-500 rounded-full" />
          </div>
        </motion.div>
      </motion.div>

      {/* ── Loading dots ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1, duration: 0.4 }}
        className="absolute bottom-14 flex gap-2"
      >
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-emerald-400"
            animate={{ scale: [1, 1.5, 1], opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1, delay: i * 0.2, repeat: Infinity }}
          />
        ))}
      </motion.div>
    </div>
  );
}
